import React, { useEffect, useMemo, useState } from "react";
import supabase from "../supabase.js";
import {
  listProjectDocuments,
  uploadProjectDocument,
  deleteProjectDocument,
  updateProjectTeam,
} from "../lib/projects.js";

export default function ProjectSettingsModal({
  isOpen,
  onClose,
  project,
  profile,
  onProjectUpdated,
}) {
  const isAdmin =
    profile?.role === "Admin" ||
    profile?.role === "superAdmin" ||
    profile?.isTrueSuperAdmin === true;

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [writers, setWriters] = useState(project?.writers || []);
  const [editors, setEditors] = useState(project?.editors || []);
  const [approvers, setApprovers] = useState(project?.approvers || []);

  const [savingTeam, setSavingTeam] = useState(false);

  // Documents
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const [file, setFile] = useState(null);
  const [fileTitle, setFileTitle] = useState("");
  const [uploading, setUploading] = useState(false);

  const [teamSavedMsg, setTeamSavedMsg] = useState("");

  useEffect(() => {
    if (!isOpen || !project?.company_id) return;

    const load = async () => {
      setLoadingUsers(true);
      try {
        const { data, error } = await supabase
          .from("Users")
          .select("id,name,email,role")
          .eq("company_id", project.company_id);

        if (error) throw error;
        setUsers(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingUsers(false);
      }
    };

    load();
  }, [isOpen, project?.company_id]);

  useEffect(() => {
    if (!isOpen || !project?.id) return;

    const loadDocs = async () => {
      setLoadingDocs(true);
      try {
        const data = await listProjectDocuments(project.id);
        setDocs(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingDocs(false);
      }
    };

    loadDocs();
  }, [isOpen, project?.id]);

  useEffect(() => {
    // keep state synced if project changes
    if (!project) return;
    setWriters(project.writers || []);
    setEditors(project.editors || []);
    setApprovers(project.approvers || []);
  }, [project]);

  const userOptions = useMemo(
    () =>
      (users || []).map((u) => ({
        id: u.id,
        label: u.name || u.email || u.id.slice(0, 8),
      })),
    [users]
  );

  const toggleId = (list, setList, id) => {
    setList((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const saveTeam = async () => {
    if (!isAdmin) return;

    try {
      setSavingTeam(true);
      setTeamSavedMsg("");

      const data = await updateProjectTeam(project.id, {
        writers,
        editors,
        approvers,
      });

      onProjectUpdated?.(data);

      setTeamSavedMsg("Team Updated");
      setTimeout(() => setTeamSavedMsg(""), 2000);
    } catch (e) {
      console.error(e);
      alert("Failed to save project team.");
    } finally {
      setSavingTeam(false);
    }
  };

  const uploadDoc = async () => {
    if (!isAdmin) return;
    // ✅ Add check to ensure fileTitle is not empty
    if (!file || !fileTitle.trim()) return;

    try {
      setUploading(true);

      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", fileTitle.trim()); // ✅ Use the required title
      fd.append("company_id", project.company_id);
      fd.append("uploaded_by", profile.id);

      const doc = await uploadProjectDocument(project.id, fd);
      setDocs((prev) => [doc, ...prev]);

      setFile(null);
      setFileTitle("");
    } catch (e) {
      console.error(e);
      alert("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = async (docId) => {
    const ok = window.confirm("Delete this document?");
    if (!ok) return;

    try {
      await deleteProjectDocument(project.id, docId);
      setDocs((prev) => prev.filter((d) => d.id !== docId));
    } catch (e) {
      console.error(e);
      alert("Failed to delete document.");
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onMouseDown={onClose}>
      <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={{ fontWeight: 900, fontSize: 16 }}>Project Settings</div>
          <button style={styles.closeBtn} onClick={onClose}>
            ✕
          </button>
        </div>

        {/* TEAM */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Team</div>
          <div style={styles.sectionSub}>
            Add Writers, Editors, and Approvers to this project.
          </div>
          {!isAdmin && (
            <div style={styles.adminNote}>
              Only Admins can edit the project team.
            </div>
          )}

          {loadingUsers ? (
            <div style={styles.muted}>Loading users…</div>
          ) : (
            <div style={styles.grid3}>
              <div>
                <div style={styles.label}>Writers</div>
                <div style={styles.pickList}>
                  {userOptions.map((u) => (
                    <label key={`w-${u.id}`} style={styles.pickRow}>
                      <input
                        type="checkbox"
                        checked={writers.includes(u.id)}
                        onChange={() => toggleId(writers, setWriters, u.id)}
                        disabled={!isAdmin}
                      />
                      <span>{u.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div style={styles.label}>Editors</div>
                <div style={styles.pickList}>
                  {userOptions.map((u) => (
                    <label key={`e-${u.id}`} style={styles.pickRow}>
                      <input
                        type="checkbox"
                        checked={editors.includes(u.id)}
                        onChange={() => toggleId(editors, setEditors, u.id)}
                        disabled={!isAdmin}
                      />
                      <span>{u.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div style={styles.label}>Approvers</div>
                <div style={styles.pickList}>
                  {userOptions.map((u) => (
                    <label key={`a-${u.id}`} style={styles.pickRow}>
                      <input
                        type="checkbox"
                        checked={approvers.includes(u.id)}
                        onChange={() => toggleId(approvers, setApprovers, u.id)}
                        disabled={!isAdmin}
                      />
                      <span>{u.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              marginTop: 12,
            }}
          >
            <button
              style={styles.primaryBtn}
              onClick={saveTeam}
              disabled={savingTeam || !isAdmin}
            >
              {savingTeam ? "Saving..." : "Save Team"}
            </button>

            {teamSavedMsg ? (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  color: "#2ecc71",
                  fontWeight: 700,
                }}
              >
                {teamSavedMsg}
              </div>
            ) : null}
          </div>
        </div>

        {/* DOCS */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Project Documents</div>
          <div style={styles.sectionSub}>
            Upload style guides, brand guidelines, PDFs, etc. These will be
            referenced during article generation.
          </div>
          {!isAdmin && (
            <div style={styles.adminNote}>
              Only Admins can upload documents.
            </div>
          )}

          <div style={styles.uploadRow}>
            <input
              style={styles.input}
              placeholder="Document title"
              value={fileTitle}
              onChange={(e) => setFileTitle(e.target.value)}
              disabled={!isAdmin}
            />
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              disabled={!isAdmin}
            />
            <button
              style={styles.primaryBtn}
              onClick={uploadDoc}
              disabled={uploading || !file || !fileTitle.trim() || !isAdmin}
            >
              {uploading ? "Uploading..." : "Upload"}
            </button>
          </div>

          {loadingDocs ? (
            <div style={styles.muted}>Loading documents…</div>
          ) : (
            <div style={styles.docList}>
              {docs.map((d) => (
                <div key={d.id} style={styles.docRow}>
                  <div style={{ fontWeight: 900, fontSize: 13 }}>{d.title}</div>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
                    {isAdmin && (
                      <button
                        style={styles.dangerLink}
                        onClick={() => removeDoc(d.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {docs.length === 0 && (
                <div style={styles.muted}>No documents uploaded yet.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  modal: {
    width: "min(980px, 96vw)",
    maxHeight: "88vh",
    overflow: "auto",
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
    padding: 16,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  closeBtn: {
    border: "1px solid #e5e7eb",
    background: "#fff",
    borderRadius: 10,
    width: 34,
    height: 34,
    cursor: "pointer",
    fontWeight: 900,
  },
  section: {
    marginTop: 14,
    border: "1px solid #eef2f7",
    borderRadius: 12,
    padding: 14,
  },
  sectionTitle: { fontWeight: 900, fontSize: 14 },
  sectionSub: {
    color: "#6b7280",
    fontSize: 13,
    marginTop: 6,
    marginBottom: 12,
    fontWeight: 700,
  },
  grid3: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
  },
  label: { fontSize: 12, color: "#6b7280", fontWeight: 900, marginBottom: 8 },
  pickList: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 10,
    maxHeight: 200,
    overflow: "auto",
  },
  pickRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    fontSize: 13,
    fontWeight: 800,
    padding: "6px 2px",
  },
  uploadRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr auto",
    gap: 10,
    alignItems: "center",
  },
  input: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 13,
    fontWeight: 800,
  },
  primaryBtn: {
    background: "#0f766e",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "10px 12px",
    fontWeight: 900,
    cursor: "pointer",
  },
  docList: { display: "flex", flexDirection: "column", gap: 10 },
  docRow: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #eef2f7",
    borderRadius: 12,
    padding: "10px 12px",
  },
  dangerLink: {
    border: "none",
    background: "transparent",
    color: "#b91c1c",
    fontWeight: 900,
    cursor: "pointer",
  },
  muted: { color: "#6b7280", fontWeight: 700, fontSize: 13 },
  adminNote: {
    color: "#b45309",
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 12,
  },
};
